import { notUndefined } from '../portable/basicTypes';
import type { BasicFetch, FetchApi, RequestInit } from '../types/basicFetch';
import type { Clock, TimeoutCookie } from '../types/clock';
import type { StructuredLogger } from '@mimir/primitive/structuredLogger';
import { asyncSleep } from '../util/asyncSleep';
import { exceptionSummary, exceptionToString } from '../util/exception';
import { redactUrl } from '../format/redactUrl';
import { invariant } from '../util/invariant';

const RETRY_DELAY_EXPONENTIATION_BASE = 2;
const SECOND_RETRY_DELAY = 1000;

export interface FetchExponentialBackoffOptions {
  signal?: AbortSignal;

  /**
   * Specifies a limit for how long a transfer is allowed to take, in milliseconds.
   *
   * If unspecified or zero, the transfer is allowed take as long as it needs.
   */
  transferTimeLimit?: number;

  /**
   * Specifies how long retry delays are allowed to last, in milliseconds.
   *
   * If unspecified the retry strategy is allowed to sleep a total of 10 seconds
   * between retries.
   *
   * The value zero means the retry strategy is not allowed to sleep and try
   * again at all.
   *
   * TODO: The comment above may be incorrect. retryDelayLimit is passed to
   * Backoff as the second argument which is total time limit. (i.e how long
   * the total allowed retry time is).
   */
  retryDelayLimit?: number;

  /**
   * Specifies the maximum number of retries. If not specified the number of
   * retries is unlimited.
   */
  maxNumberOfRetires?: number;
}

function anySignalOrUndefined(fetchApi: FetchApi, signals: AbortSignal[]): AbortSignal | undefined {
  if (signals.length < 2) {
    return signals[0];
  }

  const aborted = signals.find(signal => signal.aborted);
  if (aborted) {
    return aborted;
  }

  const ac = new fetchApi.AbortController();
  function onAbort() {
    ac.abort();
    for (const signal of signals) {
      signal.removeEventListener('abort', onAbort);
    }
  }

  for (const signal of signals) {
    signal.addEventListener('abort', onAbort);
  }
  return ac.signal;
}

let idCount = 0;

class Backoff {
  totalDelay = 0;
  failures = 0;

  constructor(
    private clock: Clock,
    private timeLimit: number,
    private abortSignal: AbortSignal | null,
    private structuredLogger: StructuredLogger | null
  ) {
  }

  async sleepUnlessTimelimitReached(info: Record<string, unknown>): Promise<boolean> {
    if (this.abortSignal?.aborted) {
      return false;
    }

    const failureCount = this.failures + 1;
    let jitteredDelay = 0;
    if (this.failures == 1) {
      jitteredDelay = Math.round(SECOND_RETRY_DELAY * (1 - Math.random() / 2));
    } else if (this.failures > 1) {
      jitteredDelay = Math.round(this.totalDelay * (RETRY_DELAY_EXPONENTIATION_BASE - Math.random() / 10));
    }

    this.totalDelay += jitteredDelay;
    if (this.totalDelay > this.timeLimit) {
      return false;
    }

    const id = 'Backoff-' + idCount++;
    this.structuredLogger?.log({ message: 'Will retry later', level: 'warn', ...info, transientError: { id, failureCount, delay: jitteredDelay / 1000 } });
    await asyncSleep(this.clock, jitteredDelay, this.abortSignal);
    this.structuredLogger?.log({ message: 'Retrying', level: 'info', ...info, transientError: { id, failureCount } });
    this.failures = Math.max(this.failures, failureCount);
    return true;
  }
}

export function resilientFetch(
  fetchApi: FetchApi,
  options: FetchExponentialBackoffOptions,
  clock: Clock,
  structuredLogger: StructuredLogger | null
): BasicFetch {
  const retryDelayLimit = options.retryDelayLimit ?? 10000;
  const transferTimeLimit = options.transferTimeLimit ?? 0;
  return async (url: string, init: RequestInit) => {
    invariant(url !== null, 'resilientFetch expected url');

    let timeoutId: TimeoutCookie | undefined = undefined;
    let transferTimeoutSig: AbortSignal | undefined = undefined;
    if (transferTimeLimit > 0) {
      // Could use the built in AbortSignal.timeout here, but we want
      // control over the clock for testing.
      const controller = new fetchApi.AbortController();
      transferTimeoutSig = controller.signal;
      timeoutId = clock.setTimeout(() => {
        structuredLogger?.log({
          message: 'Fetch timeout',
          url,
          transferTimeLimit
        });
        controller.abort();
      }, transferTimeLimit);
    }

    const signalCandidates: (AbortSignal | undefined)[] = [options.signal, init.signal, transferTimeoutSig];
    const signal = anySignalOrUndefined(fetchApi, signalCandidates.filter(notUndefined));
    init = {
      ...init,
      signal,
    };
    const backoff = new Backoff(clock, retryDelayLimit, init.signal ?? null, structuredLogger);
    try {
      return await fetchWithRetry(url, init, backoff, fetchApi, structuredLogger, options.maxNumberOfRetires);
    } finally {
      if (timeoutId !== undefined) {
        clock.clearTimeout(timeoutId);
      }
    }
  };
}

async function fetchWithRetry(
  url: string,
  init: RequestInit,
  backoff: Backoff,
  fetchApi: FetchApi,
  structuredLogger: StructuredLogger | null,
  maxNumberOfRetries: number | undefined,
) {
  let retryCounter = 0;
  while (true) {
    try {
      const response = await fetchApi.fetch(url, init);
      if (!response.ok) {
        retryCounter += 1;
        if ((maxNumberOfRetries !== undefined) && (retryCounter > maxNumberOfRetries)) {
          structuredLogger?.log({ message: 'Max number of retires reached', level: 'warn', maxNumberOfRetries });
          return response;
        }
        if ((response.status >= 500) || (response.status === 429)) {
          const info: Record<string, unknown> = {
            op: 'HTTP fetch',
            status: response.status,
            statusText: response.statusText,
            url: redactUrl(url)
          };

          const shouldRetry = await backoff.sleepUnlessTimelimitReached(info);
          if (shouldRetry) {
            continue;
          }
        }
      }
      return response;
    } catch(ex) {
      const redactedUrl = redactUrl(url);
      structuredLogger?.log({
        message: `Fetch of ${init.method ?? 'GET'} ${redactedUrl} failed: ${exceptionToString(ex)}`,
        level: 'warn',
      });
      if (ex == null || typeof ex !== 'object') {
        throw ex;
      }

      const err = ex as { name?: unknown };
      if (err.name === 'NetworkError' || err.name === 'TypeError' || err.name === 'FetchError') {
        const info: Record<string, unknown> = {
          op: 'HTTP fetch',
          url: redactUrl(url),
          error: exceptionSummary(ex)
        };
        const shouldRetry = await backoff.sleepUnlessTimelimitReached(info);
        if (shouldRetry) {
          continue;
        }
      }

      throw ex;
    }
  }
}
